import { Inject, Injectable } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import {
  AmdocsAuthService,
  AmdocsApiService,
  AmdocsTimeService,
  ILoginCredentials,
  ILoginGatewayConfig,
  AMDOCS_CONSTANTS
} from 'amdocs-auth-package';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, mergeMap } from 'rxjs/operators';
import { ApiError } from 'amdocs-core-package';

/**
 * Extended AmdocsAuthService that overrides setLoginCredentials functionality
 * Since setLoginCredentials is private, we override the public methods that call it:
 * - login()
 * - callRefreshUserToken()
 */
@Injectable()
export class CustomAmdocsAuthService extends AmdocsAuthService {

  constructor(
    @Inject('ILoginGatewayConfig') loginGatewayConfig: ILoginGatewayConfig,
    apiService: AmdocsApiService,
    timeService: AmdocsTimeService,
    cookieService: CookieService
  ) {
    super(loginGatewayConfig, apiService, timeService, cookieService);
  }

  /**
   * Override login method to use custom setLoginCredentials
   */
  public login(code: string): Observable<ILoginCredentials> {
    return this['apiService'].login(code).pipe(
      mergeMap((response: ILoginCredentials): Observable<ILoginCredentials> => {
        // Call custom setLoginCredentials instead of private one
        this.customSetLoginCredentials(response);
        return of(response);
      })
    );
  }

  /**
   * Override callRefreshUserToken to use custom setLoginCredentials
   */
  public callRefreshUserToken(): Observable<ILoginCredentials> {
    return this['apiService'].refreshToken().pipe(
      mergeMap((response: ILoginCredentials) => {
        // Call custom setLoginCredentials instead of private one
        this.customSetLoginCredentials(response);
        return of(response);
      }),
      catchError((error) => {
        this.initGetLoginUrlFlow().subscribe();
        return throwError(error);
      })
    );
  }

  /**
   * Custom implementation of setLoginCredentials
   * Modify this method to change the behavior as needed
   */
  private customSetLoginCredentials(loginCredentials: ILoginCredentials): void {
    // const stsCreds = loginCredentials.stsCredentials;
    const options = {
      path: '/',
      secure: true,
      expires: new Date(new Date(loginCredentials.idTokenExpiration).getTime())
    };
    const casTokenOptions = {
      path: '/',
      secure: true,
      expires: new Date(new Date(loginCredentials.casTokenExpiration).getTime())
    };

    // Set STS credentials
    // this['cookieService'].set(
    //   AMDOCS_CONSTANTS.ACCESS_KEY_ID,
    //   stsCreds.accessKeyId,
    //   options
    // );
    // this['cookieService'].set(
    //   AMDOCS_CONSTANTS.SECRET_ACCESS_KEY,
    //   stsCreds.secretAccessKey,
    //   options
    // );
    // this['cookieService'].set(
    //   AMDOCS_CONSTANTS.SESSION_TOKEN,
    //   stsCreds.sessionToken,
    //   options
    // );
    // this['cookieService'].set(
    //   AMDOCS_CONSTANTS.EXPIRATION,
    //   stsCreds.expiration,
    //   options
    // );

    // Set token credentials
    this['cookieService'].set(
      AMDOCS_CONSTANTS.TOKEN_KEY,
      loginCredentials.idToken,
      options
    );
    this['cookieService'].set(
      AMDOCS_CONSTANTS.TOKEN_EXPIRATION_KEY,
      loginCredentials.idTokenExpiration.toString(),
      options
    );
    this['cookieService'].set(
      AMDOCS_CONSTANTS.CAS_TOKEN_EXPIRATION_KEY,
      loginCredentials.casTokenExpiration.toString(),
      casTokenOptions
    );

    // Set app token - THIS IS WHERE YOU CAN MODIFY THE BEHAVIOR
    // Example: You can change the expiration or add custom logic here
    this['cookieService'].set(
      AMDOCS_CONSTANTS.APP_TOKEN,
      loginCredentials.appToken,
      {
        ...options,
        expires: new Date(loginCredentials.casTokenExpiration)
        // Add your custom logic here
        // For example: expires: new Date(Date.now() + 24 * 60 * 60 * 1000) // 24 hours from now
      }
    );

    // Add any additional custom logic here
    // For example: logging, analytics, custom cookie settings, etc.
  }
}
